import flagsmith from "flagsmith";
import { buildFeatureFlags, type FeatureFlags } from "constants/featureFlags";
import type React from "react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

type FeatureFlagName = keyof FeatureFlags;

interface FeatureFlagsProviderProps {
  children: ReactNode;
  overrides?: Partial<FeatureFlags>;
}

interface FeatureFlagsContextValue extends FeatureFlags {
  isEnabled: (flag: FeatureFlagName) => boolean;
}

const createContextValue = (flags: FeatureFlags, overrides?: Partial<FeatureFlags>): FeatureFlagsContextValue => {
  const merged = { ...flags, ...overrides };
  return {
    ...merged,
    isEnabled: (flag: FeatureFlagName) => Boolean(merged[flag]),
  };
};

const defaultValue = createContextValue(buildFeatureFlags());

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(defaultValue);

const flagsmithToFlags = (fallback: FeatureFlags): FeatureFlags => {
  // Reading flags runs inside onChange, i.e. inside a React state update. A throw here would
  // propagate to the ErrorBoundary this provider sits under, so it is guarded like init below.
  try {
    return {
      IMAGE_LIBRARY: flagsmith.hasFeature("image_library"),
      HIDE_ENDPOINT_MARKERS: flagsmith.hasFeature("hide_endpoint_markers"),
      PRICING_ENABLED: flagsmith.hasFeature("pricing_enabled"),
      ROUTE_LINE_CASING: flagsmith.hasFeature("route_line_casing"),
      PLACES_AUTOCOMPLETE_CASCADE: flagsmith.hasFeature("places_autocomplete_cascade"),
    };
  } catch (e) {
    console.error("[FeatureFlags] reading flags failed, keeping current values:", e);
    return fallback;
  }
};

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children, overrides }) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => buildFeatureFlags());

  // 123yb692j0w — a flag-service outage must degrade to "flags off", never take the app down.
  //
  // flags.stippl.dev intermittently returned a real HTTP 500 (its own error HTML, not a timeout),
  // and loading any /trip page crashed to the ErrorBoundary — which this provider sits INSIDE, so
  // anything escaping this effect kills the whole app rather than one feature. `onError` alone was
  // not enough: it catches what the SDK routes to it, not a synchronous throw here or a rejection
  // from the promise init() returns, and nothing was attached to either.
  //
  // Both are covered now, and `flags` state is seeded from buildFeatureFlags() (env defaults), so
  // a total failure leaves the app running on defaults.
  useEffect(() => {
    const envKey = import.meta.env.VITE_FLAGSMITH_ENV_KEY;
    if (!envKey) return;
    try {
      // cacheFlags persists the last successful fetch, so a blip serves the previous values
      // instead of dropping every flag back to its default mid-session.
      const started = flagsmith.init({
        environmentID: envKey,
        api: "https://flags.stippl.dev/api/v1/",
        cacheFlags: true,
        onChange: () => setFlags((current) => flagsmithToFlags(current)),
        onError: (e) => console.error("[FeatureFlags] Flagsmith init failed:", e),
      });
      // init() returns a promise in v10. The SDK calls onError and then re-throws, so that promise
      // rejects a second time — without this catch it escapes as an unhandled rejection (Sentry
      // JAVASCRIPT-REACT-84, ~1,200 events a week, every one a 500 from flags.stippl.dev; the
      // /trip crash in 123yb692j0w). Promise.resolve tolerates a void return without assuming
      // the contract.
      Promise.resolve(started).catch((e) => console.error("[FeatureFlags] Flagsmith init rejected:", e));
    } catch (e) {
      console.error("[FeatureFlags] Flagsmith init threw, continuing on default flags:", e);
    }
  }, []);

  const value = useMemo(() => createContextValue(flags, overrides), [flags, overrides]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  return useContext(FeatureFlagsContext);
};

export const useFeatureFlag = (flag: FeatureFlagName): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag);
};

export const FeatureFlag: React.FC<{ name: FeatureFlagName; fallback?: ReactNode; children?: ReactNode }> = ({ name, fallback = null, children }) => {
  const enabled = useFeatureFlag(name);
  if (!enabled) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
};
